import { useState, useEffect } from "react";
import type { ColumnsType } from "antd/es/table";
import TableList from "../../ui-component/Table";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import { useLocation, useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";
import { useTranslation } from "react-i18next";

interface Providers {
  id?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: null;
  logTracker?: string;
  name?: { en: string; ar?: string };
  membershipPlan?: {
    name?: { en: string; ar?: string };
    colors?: {
      backgroundEndColor: string;
      backgroundStartColor: string;
      textColor: string;
    };
  };
  email?: string;
  logo?: string;
  phoneNumbers?: string[];

  companySize?: string;
  type?: string;
  status?: string;
  imageUrl?: string;
  phoneNumber?: string;
  contactPerson?: {
    name?: string;
    email?: string;
    countryCode?: string;
    phoneNumber?: string;
  };

  industry?: string;
  isActive?: boolean;

  description?: { en: string; ar?: string };
}

const CompaniesList = () => {
  const { t, i18n } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [searchKey, setSearchKey] = useState<string>();
  const [companies, setCompanies] = useState<Providers[]>([]);
  const [limit] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();

  const CompaniesColumns: ColumnsType<Providers> = [
    {
      title: t("companies.company"),
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (_, record) => (
        <div
          className="title-with-img"
          onClick={() => navigate(`/add-company/${record?.id}`)}
        >
          <div className={`img-container ${record.isActive ? "active" : ""}`}>
            <span>
              <img
                src={
                  record?.logo ||
                  record?.imageUrl ||
                  "/assets/providers/mokhtabar.png"
                }
                alt={record.name?.[i18n.language || "en"]}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/assets/providers/mokhtabar.png";
                }}
              />
            </span>
          </div>
          <p>{record.name?.[i18n.language || "en"]}</p>
        </div>
      ),
    },
    {
      title: t("providers.table.contactPerson"),
      dataIndex: "contactPerson",
      key: "contactPerson",
      render: (_, record) => <p>{record.contactPerson?.name || "-"}</p>,
    },
    {
      title: t("providers.table.contactEmail"),
      dataIndex: "id",
      key: "contactEmail",
      render: (_, record) => (
        <p>{record.contactPerson?.email || record?.email || "-"}</p>
      ),
      width: 200,
    },
    {
      title: t("providers.table.contactPhone"),
      dataIndex: "cr",
      key: "contactPhone",
      render: (_, record) => (
        <p>{record.contactPerson?.phoneNumber || record?.phoneNumber || "-"}</p>
      ),
    },
    {
      title: t("providers.table.status"),
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <p style={{ textTransform: "capitalize" }}>
          {record.isActive ? t("shared.active") : t("shared.inActive")}
        </p>
      ),
    },
    {
      title: t("companies.employees"),
      dataIndex: "companySize",
      key: "companySize",
      render: (_, record) => (
        <p style={{ textTransform: "capitalize" }}>
          {record.companySize || "0"}
        </p>
      ),
    },
    {
      title: t("companies.package"),
      dataIndex: "membershipPlan",
      key: "membershipPlan",
      render: (_, record) => (
        <p
          style={{
            padding: "5px 10px",
            borderRadius: "20px",
            display: "inline-flex",
            textTransform: "capitalize",
            color: record?.membershipPlan?.colors?.textColor,
            background: record.membershipPlan.colors?.backgroundEndColor,
          }}
        >
          {record?.membershipPlan?.name?.[i18n.language || "en"] || "0"}
        </p>
      ),
    },
  ];

  const { data, isLoading } = useGetData<{
    data: { items: Providers[]; count: number };
  }>({
    queryKey: ["sponsor", { offset, limit, searchKey, location }],
    url: "/sponsor",
    params: {
      offset,
      limit,
      searchKey,
      withDeleted: false,
      sortKey: "createdAt:-1",
    },
  });

  useEffect(() => {
    if (data?.data?.items && !!searchKey) {
      setCompanies(data.data.items);
      return;
    }

    if (data?.data?.items) {
      setCompanies((prevProviders) => [...prevProviders, ...data.data.items]);
    }
  }, [data]);

  const loadMoreItems = () => {
    if (data && companies.length < data.data.count && !isLoading) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  };

  const providerList = companies;
  return (
    <>
      <Header
        title={t("companies.companiesManagement")}
        headerAction={{
          actionTitle: t("companies.addCompany"),
          onAction: () => navigate("/add-company"),
        }}
        search={{
          placeholder: t("companies.searchCompany"),
          searchAction: (val) => {
            offset > 0 && setOffset(0);
            setTimeout(() => {
              setSearchKey(val);
            }, 300);
          },
        }}
      />
      <TableList
        data={uniqBy(providerList, "id")}
        columns={CompaniesColumns}
        loading={searchKey && isLoading}
        rowKey="id"
        loadMore={loadMoreItems}
        isLoadMore={isLoading}
      />
    </>
  );
};

export default CompaniesList;
